// App.jsx - Updated with sidebar collapse support
import { useState, useRef, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import API_BASE_PATH from './apiConfig';
import axios from "axios";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import Trades from "./pages/Trades";
import Login from './pages/Login';
import ImportOrdersModal from "./components/ImportOrdersModal";
import ManageAccountsModal from "./components/ManageAccountsModal";
import Debts from './pages/Debts';
import Budget from './pages/Budget';
import Bills from './pages/Bills';
import BillDetail from './pages/BillDetail';
import Transactions from './pages/Transactions';
import { configureAmplify, useAuth, getCurrentUser } from './auth';
import "./styles.css";

// Configure Amplify and axios once at startup
const initializeApp = () => {
  configureAmplify({
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
    region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
    oauthDomain: import.meta.env.VITE_COGNITO_USER_POOL_DOMAIN
  });

  axios.interceptors.request.use(
    async (config) => {
      try {
        const session = await getCurrentUser();
        if (session?.tokens?.idToken) {
          config.headers.Authorization = `Bearer ${session.tokens.idToken.toString()}`;
        }
      } catch (error) {
        // Silent fail - user might be on public route
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

initializeApp();

// Protected Routes Component
const ProtectedRoutes = ({ fetchOrdersRef }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/orders" element={<Orders fetchOrdersRef={fetchOrdersRef} />} />
      <Route path="/trades" element={<Trades />} />
      <Route path="/debts" element={<Debts />} />
      <Route path="/budget" element={<Budget />} />
      <Route path="/bills" element={<Bills />} />
      <Route path="/bills/:id" element={<BillDetail />} />
      <Route path="/transactions" element={<Transactions />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main Layout Component - Updated to handle sidebar collapse
const MainLayout = ({ children, onImportClick, onAddTransaction, onManageAccounts }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  // Listen for sidebar state changes
  useEffect(() => {
    const checkSidebarState = () => {
      const isCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
      setSidebarCollapsed(isCollapsed);
    };

    // Check on storage events (from other tabs)
    window.addEventListener('storage', checkSidebarState);

    // Also check periodically to catch same-tab changes
    const interval = setInterval(checkSidebarState, 100);

    return () => {
      window.removeEventListener('storage', checkSidebarState);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="app-container">
      <Header />
      <div className="app-body">
        <Sidebar
          onImportClick={onImportClick}
          onAddTransaction={onAddTransaction}
          onManageAccounts={onManageAccounts}
        />
        <div className={`content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="main-content">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// App Content Component
const AppContent = ({ fetchOrdersRef }) => {
  const location = useLocation();
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showManageAccounts, setShowManageAccounts] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [categories, setCategories] = useState([]);

  const isLoginPage = location.pathname === '/login';

  // Fetch data for modals when needed
  useEffect(() => {
    if (showAddTransaction) {
      fetchModalData();
    }
  }, [showAddTransaction]);

  const fetchModalData = async () => {
    try {
      const [accountsRes, debtsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_BASE_PATH}/accounts`),
        axios.get(`${API_BASE_PATH}/debts`),
        axios.get(`${API_BASE_PATH}/budget-categories`)
      ]);
      setAccounts(accountsRes.data || []);
      setDebts(debtsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching modal data:', error);
    }
  };

  const handleImportComplete = () => {
    setShowImportPopup(false);
    if (location.pathname === "/orders" && fetchOrdersRef.current) {
      fetchOrdersRef.current();
    }
  };

  const handleTransactionSave = () => {
    setShowAddTransaction(false);
    setRefreshKey(prev => prev + 1);
  };

  const handleAccountsSave = () => {
    setShowManageAccounts(false);
    setRefreshKey(prev => prev + 1);
  };

  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  return (
    <>
      <MainLayout
        onImportClick={() => setShowImportPopup(true)}
        onAddTransaction={() => setShowAddTransaction(true)}
        onManageAccounts={() => setShowManageAccounts(true)}
      >
        <ProtectedRoutes fetchOrdersRef={fetchOrdersRef} key={refreshKey} />
      </MainLayout>

      {showImportPopup && (
        <ImportOrdersModal
          onClose={() => setShowImportPopup(false)}
          onImportComplete={handleImportComplete}
        />
      )}

      {showAddTransaction && (
        <AddTransactionModal
          accounts={accounts}
          debts={debts}
          categories={categories}
          onClose={() => setShowAddTransaction(false)}
          onSave={handleTransactionSave}
        />
      )}

      {showManageAccounts && (
        <ManageAccountsModal
          onClose={() => setShowManageAccounts(false)}
          onSave={handleAccountsSave}
        />
      )}
    </>
  );
};

// Main App Component
const App = () => {
  const fetchOrdersRef = useRef(null);
  
  return (
    <Router>
      <AppContent fetchOrdersRef={fetchOrdersRef} />
    </Router>
  );
};

export default App;